import { useMemo, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, Loader2, RefreshCw, Search, Wrench } from "lucide-react";
import {
  getFeedHealth,
  queueFeedRepair,
  type FeedNewSubscriber,
  type FeedRetailerSnapshot,
  type FeedStatus,
  type FeedStoreRow,
  type FeedUserRow,
} from "../../services/FeedHealthService";

// Admin → Monitor → Feed health. Answers two questions: is any paying user's
// Walmart / Home Depot feed broken right now, and did new subscribers get
// their deals. Data comes from the feed watchdog snapshot (refreshed every
// 10 minutes; exact counts hourly).

const CARD = "bg-white dark:bg-dark-800 rounded-xl border border-gray-200 dark:border-dark-700";
const TH = "px-5 py-2.5 text-xs font-semibold text-gray-500 dark:text-dark-300 text-left";

const STATUS_STYLE: Record<FeedStatus, { pill: string; label: string }> = {
  empty: { pill: "bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300", label: "Empty feed" },
  partial: { pill: "bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300", label: "Store empty" },
  "new-waiting": { pill: "bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300", label: "New · waiting" },
  thin: { pill: "bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300", label: "Thin feed" },
  ok: { pill: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300", label: "OK" },
};

const RETAILER_LABEL: Record<string, string> = { walmart: "Walmart", home_depot: "Home Depot" };

const ago = (ms?: number) => {
  if (!ms) return "—";
  const minutes = Math.max(0, Math.round((Date.now() - ms) / 60000));
  if (minutes < 60) return `${minutes}m ago`;
  if (minutes < 48 * 60) return `${Math.round(minutes / 60)}h ago`;
  return `${Math.round(minutes / 1440)}d ago`;
};
const num = (n: number) => (n < 0 ? "…" : n.toLocaleString());

function Pill({ className, children }: { className: string; children: ReactNode }) {
  return <span className={`inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-semibold ${className}`}>{children}</span>;
}

function StatusPill({ status }: { status: FeedStatus }) {
  const s = STATUS_STYLE[status] ?? STATUS_STYLE.ok;
  return <Pill className={s.pill}>{s.label}</Pill>;
}

function Stat({ label, value, tone }: { label: string; value: string | number; tone?: "bad" | "warn" }) {
  const border = tone === "bad" ? "border-red-200 dark:border-red-900" : "border-gray-200 dark:border-dark-700";
  const color = tone === "bad" ? "text-red-700 dark:text-red-400" : tone === "warn" ? "text-amber-700 dark:text-amber-400" : "text-gray-900 dark:text-white";
  return (
    <div className={`bg-white dark:bg-dark-800 rounded-xl border ${border} px-5 py-4`}>
      <p className={`text-sm ${tone === "bad" ? color : "text-gray-500 dark:text-dark-300"}`}>{label}</p>
      <p className={`mt-1 text-3xl font-bold tabular-nums ${color}`}>{value}</p>
    </div>
  );
}

function OpenUser({ userId, onUserSelect }: { userId: string; onUserSelect?: (userId: string) => void }) {
  if (!onUserSelect) return null;
  return (
    <button
      type="button"
      onClick={() => onUserSelect(userId)}
      className="rounded-lg border border-gray-200 dark:border-dark-600 px-3 py-1.5 text-xs font-semibold text-gray-800 dark:text-dark-100 hover:bg-gray-50 dark:hover:bg-dark-700 whitespace-nowrap"
    >
      Open user
    </button>
  );
}

function SectionHeader({ title, subtitle, children }: { title: string; subtitle?: string; children?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
      <div>
        <h3 className="text-base font-semibold text-gray-900 dark:text-white">{title}</h3>
        {subtitle && <p className="text-sm text-gray-500 dark:text-dark-300">{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

function feedRatio(row: { visible: number; baseline: number }) {
  if (row.visible < 0 || row.baseline <= 0) return null;
  return Math.round((row.visible / row.baseline) * 100);
}

function firstProductsMinutes(n: FeedNewSubscriber) {
  return n.firstProductsAt ? Math.max(0, Math.round((n.firstProductsAt - n.entitledAt) / 60000)) : null;
}

export default function FeedHealthTab({ onUserSelect }: { onUserSelect?: (userId: string) => void }) {
  const [retailer, setRetailer] = useState<"walmart" | "home_depot">("walmart");
  const [query, setQuery] = useState("");
  const [repairing, setRepairing] = useState<string | null>(null);
  const [repairNote, setRepairNote] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ["admin-feed-health"],
    queryFn: getFeedHealth,
    refetchInterval: 60_000,
  });

  const snap: FeedRetailerSnapshot | undefined = data?.snapshot.retailers.find((r) => r.retailer === retailer);

  const problemUsers = useMemo(() => (snap?.userRows ?? []).filter((u) => u.status !== "ok"), [snap]);

  // Searching looks across every paying user, not just the broken ones.
  const searchQuery = query.trim().toLowerCase().replace(/^#/, "");
  const users = useMemo(() => {
    if (!snap) return [] as FeedUserRow[];
    if (!searchQuery) return problemUsers;
    return snap.userRows.filter((u) => u.userId.toLowerCase().includes(searchQuery) || u.stores.some((s) => s === searchQuery));
  }, [snap, problemUsers, searchQuery]);

  const badStores = useMemo(() => (snap?.storeRows ?? []).filter((s) => s.status !== "ok" || s.repair), [snap]);

  const repair = async (store: FeedStoreRow) => {
    setRepairing(store.storeId);
    setRepairNote(null);
    try {
      const res = await queueFeedRepair(retailer, store.storeId);
      setRepairNote(res.queued ? `Store ${store.storeId} queued — a crawler picks it up within 10 minutes.` : `Not queued (${res.reason}).`);
      void queryClient.invalidateQueries({ queryKey: ["admin-feed-health"] });
    } catch (err) {
      setRepairNote(err instanceof Error ? err.message : "Repair request failed");
    } finally {
      setRepairing(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-dark-300 py-10 justify-center">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading feed health…
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className={`${CARD} p-6 flex items-start gap-3`}>
        <AlertCircle className="w-5 h-5 text-red-500 shrink-0" />
        <div>
          <p className="font-semibold text-gray-900 dark:text-white">Feed watchdog has no data</p>
          <p className="text-sm text-gray-500 dark:text-dark-300 mt-1">
            {error instanceof Error ? error.message : "No snapshot yet."} The watchdog runs every 10 minutes on the monitoring host (feed-watch).
          </p>
        </div>
      </div>
    );
  }

  const newSubs = snap?.newSubscribers ?? [];
  const waiting = newSubs.filter((n) => !n.firstProductsAt).length;

  return (
    <div className="space-y-5 max-w-6xl">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="inline-flex rounded-lg p-1 bg-gray-100 dark:bg-dark-700">
          {(["walmart", "home_depot"] as const).map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => setRetailer(r)}
              className={`px-4 py-1.5 text-sm rounded-md ${retailer === r ? "bg-white dark:bg-dark-900 font-semibold text-gray-900 dark:text-white shadow-sm" : "font-medium text-gray-600 dark:text-dark-300"}`}
            >
              {RETAILER_LABEL[r]}
            </button>
          ))}
        </div>
        <p className={`ml-auto text-sm ${data.stale ? "text-red-600 dark:text-red-400 font-semibold" : "text-gray-500 dark:text-dark-300"}`}>
          {data.stale ? "Watchdog is not reporting — last update " : "Updated "}
          {ago(data.snapshot.generatedAt)}
        </p>
        <button
          type="button"
          onClick={() => void refetch()}
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-blue-700 dark:text-blue-400 hover:text-blue-900"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${isFetching ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {snap && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Stat label="Paying users" value={snap.users.toLocaleString()} />
            <Stat label="Broken right now" value={problemUsers.length} tone={problemUsers.length ? "bad" : undefined} />
            <Stat label="New subscribers (48h)" value={newSubs.length} />
            <Stat label="Still waiting for deals" value={waiting} tone={waiting ? "warn" : undefined} />
          </div>

          {/* Broken users */}
          <section className={`${CARD} overflow-hidden`}>
            <SectionHeader
              title={searchQuery ? "Search results" : "Paying users with a problem"}
              subtitle={searchQuery ? `Every paying ${RETAILER_LABEL[retailer]} user matching “${query.trim()}”` : "Empty feed, a store with no products, or a thin feed"}
            >
              <label className="flex items-center gap-2 w-72 max-w-full rounded-lg border border-gray-200 dark:border-dark-600 px-3 text-gray-400">
                <Search className="w-4 h-4 shrink-0" />
                <span className="sr-only">Search users</span>
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search any user by email or store #"
                  className="flex-1 min-w-0 border-0 bg-transparent py-2 text-sm text-gray-900 dark:text-white focus:ring-0 placeholder:text-gray-400"
                />
              </label>
            </SectionHeader>
            {users.length === 0 ? (
              <p className="border-t border-gray-100 dark:border-dark-700 px-5 py-6 text-sm text-emerald-700 dark:text-emerald-400 font-medium">
                {searchQuery ? "No users match." : `Every paying ${RETAILER_LABEL[retailer]} user has products right now.`}
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[760px]">
                  <thead className="bg-gray-50 dark:bg-dark-900/40">
                    <tr>
                      <th className={TH}>User</th>
                      <th className={TH}>Problem</th>
                      <th className={TH}>Stores</th>
                      <th className={`${TH} text-right`}>Products</th>
                      <th className={TH} />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-dark-700">
                    {users.slice(0, 300).map((u) => {
                      const ratio = feedRatio(u);
                      return (
                        <tr key={u.userId} className="align-top">
                          <td className="px-5 py-3.5 max-w-[280px]">
                            <p className="font-semibold text-gray-900 dark:text-white truncate">{u.userId}</p>
                            <p className="text-xs text-gray-500 dark:text-dark-400">
                              {u.plans.join(", ")}{u.new ? " · new" : ""}
                            </p>
                          </td>
                          <td className="px-5 py-3.5"><StatusPill status={u.status} /></td>
                          <td className="px-5 py-3.5 text-gray-600 dark:text-dark-300">
                            <div className="flex flex-wrap gap-x-1.5">
                              {u.stores.map((s) => (
                                <span key={s} className={u.emptyStores?.includes(s) ? "text-red-700 dark:text-red-400 font-semibold" : u.thinStores?.includes(s) ? "text-amber-700 dark:text-amber-400 font-semibold" : ""}>
                                  #{s}
                                </span>
                              ))}
                            </div>
                            {u.reason && <p className="mt-0.5 text-xs text-gray-500 dark:text-dark-400 leading-snug">{u.reason}</p>}
                          </td>
                          <td className="px-5 py-3.5 text-right tabular-nums whitespace-nowrap">
                            <span className="font-semibold text-gray-900 dark:text-white">{num(u.visible)}</span>
                            {u.baseline > 0 && (
                              <span className="block text-xs text-gray-500 dark:text-dark-400">
                                usually {u.baseline.toLocaleString()}{ratio !== null && ratio < 80 ? ` (${ratio}%)` : ""}
                              </span>
                            )}
                          </td>
                          <td className="px-5 py-3.5 text-right"><OpenUser userId={u.userId} onUserSelect={onUserSelect} /></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {users.length > 300 && <p className="px-5 py-2 text-xs text-gray-500">Showing 300 of {users.length}. Search to narrow.</p>}
              </div>
            )}
          </section>

          {/* New subscribers */}
          <section className={`${CARD} overflow-hidden`}>
            <SectionHeader title="New subscribers · last 48h" subtitle="Did they get products and the “your deals are in” push?" />
            {newSubs.length === 0 ? (
              <p className="border-t border-gray-100 dark:border-dark-700 px-5 py-6 text-sm text-gray-500 dark:text-dark-300">None in the last 48 hours.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[760px]">
                  <thead className="bg-gray-50 dark:bg-dark-900/40">
                    <tr>
                      <th className={TH}>User</th>
                      <th className={TH}>Stores</th>
                      <th className={TH}>Subscribed</th>
                      <th className={TH}>First products</th>
                      <th className={TH}>Push</th>
                      <th className={TH} />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-dark-700">
                    {newSubs.slice(0, 50).map((n) => {
                      const minutes = firstProductsMinutes(n);
                      const waitingMin = Math.round((Date.now() - n.entitledAt) / 60000);
                      return (
                        <tr key={n.userId}>
                          <td className="px-5 py-3.5 max-w-[280px] font-semibold text-gray-900 dark:text-white truncate">{n.userId}</td>
                          <td className="px-5 py-3.5 text-gray-600 dark:text-dark-300">{n.stores.map((s) => `#${s}`).join(" ")}</td>
                          <td className="px-5 py-3.5 text-gray-600 dark:text-dark-300 whitespace-nowrap">{ago(n.entitledAt)}</td>
                          <td className="px-5 py-3.5">
                            {minutes !== null ? (
                              <Pill className={minutes <= 10 ? STATUS_STYLE.ok.pill : STATUS_STYLE.thin.pill}>After {minutes}m</Pill>
                            ) : (
                              <Pill className={waitingMin >= 15 ? STATUS_STYLE.empty.pill : STATUS_STYLE.thin.pill}>Waiting {waitingMin}m</Pill>
                            )}
                          </td>
                          <td className="px-5 py-3.5 text-gray-600 dark:text-dark-300">{n.pushNote ? n.pushNote.charAt(0).toUpperCase() + n.pushNote.slice(1) : n.firstPushAt ? "Sent" : "Not yet"}</td>
                          <td className="px-5 py-3.5 text-right"><OpenUser userId={n.userId} onUserSelect={onUserSelect} /></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* Stores needing attention */}
          <section className={`${CARD} overflow-hidden`}>
            <SectionHeader title="Stores needing attention" subtitle={badStores.length ? undefined : `All ${snap.stores} stores have a normal feed.`}>
              {snap.repairQueued > 0 && (
                <span className={`text-sm ${snap.repairOldestMin >= 30 ? "text-red-700 font-semibold" : "text-gray-500 dark:text-dark-300"}`}>
                  Repair queue: {snap.repairQueued} · oldest {snap.repairOldestMin}m
                </span>
              )}
            </SectionHeader>
            {repairNote && <p className="px-5 pb-3 text-sm text-gray-600 dark:text-dark-300">{repairNote}</p>}
            {badStores.length > 0 && (
              <ul className="divide-y divide-gray-100 dark:divide-dark-700 border-t border-gray-100 dark:border-dark-700">
                {badStores.slice(0, 50).map((s) => (
                  <li key={s.storeId} className="px-5 py-3 flex items-center gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-semibold text-gray-900 dark:text-white">Store #{s.storeId}</span>
                        <StatusPill status={s.status} />
                        <span className="text-xs text-gray-500">{s.users} user{s.users === 1 ? "" : "s"}</span>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-dark-300 mt-0.5">
                        {num(s.visible)} products now{s.baseline ? `, usually ~${s.baseline.toLocaleString()}` : ""} · newest {ago(s.newestAt)}
                        {s.repair &&
                          ` · repair ${s.repair.finishedAt ? (s.repair.ok ? "finished" : `failed (${s.repair.error ?? "error"})`) : s.repair.startedAt ? `running on ${s.repair.host}` : "queued"} ${ago(s.repair.finishedAt || s.repair.startedAt || s.repair.requestedAt)}`}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => void repair(s)}
                      disabled={repairing === s.storeId}
                      className="shrink-0 inline-flex items-center gap-1.5 rounded-lg border border-gray-200 dark:border-dark-600 px-3 py-1.5 text-xs font-semibold text-gray-800 dark:text-dark-100 hover:bg-gray-50 dark:hover:bg-dark-700 disabled:opacity-50"
                    >
                      {repairing === s.storeId ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wrench className="w-3 h-3" />} Re-crawl
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      )}
    </div>
  );
}
